// Layer attribute expression editor

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, SelectChangeEvent, Stack, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import AttributeHelp from "../../../Components/AttributeHelp";
import { IsNumber } from "../../../Globals";
import { ParcelSearchSelect } from "../../../Parcels/ParcelSearch";
import { ParcelSearchTextField } from "../../../Parcels/ParcelSearchBuilder";
import { ToastNotification } from "../../../ToastNotifications";
import { ILayer, IVectorLayerAttribute } from "../../../Layers/LayerInterfaces";
import { ILayerAttribExpression, ILayerAttribExpressionClause, LayerAttribExpressionNumberOperators, LayerAttribExpressionStringOperators, TLayerAttribExpressionStringOperator } from "./LayerAttribExpressionInterfaces";
import { GetVectorLayerAttributeByID } from "../../../AttributeOps";
import { theme_bgColorLight1, theme_textColorMain, theme_errorRed, theme_bgColorMain, theme_limeGreen, theme_textColorBlended, theme_bgColorGradient2 } from "../../../Theme";


const LAYER_ATTRIBUTE_EXPRESSION_MAX_CLAUSES: number = 10;


//-------------------------------------------------------------------------------
// Component props
//-------------------------------------------------------------------------------
export interface LayerAttribExpressionEditorProps
{
  layer?: ILayer;
  showExpressionEditor: boolean;
  setShowExpressionEditor: any;
  expression?: ILayerAttribExpression;  // input
  OnExpressionChanged: any; // output
}

//-------------------------------------------------------------------------------
// Layer attribute expression editor
//-------------------------------------------------------------------------------
export function LayerAttribExpressionEditor(props: LayerAttribExpressionEditorProps)
{
  // Get needed state data from the store
  // const { 
  //       } = useStore();


  const [localExpression, setLocalExpression] = useState<ILayerAttribExpression|undefined>(undefined);
  const [localChangesWereMade, setLocalChangesWereMade] = useState<boolean>(false);









  //-------------------------------------------------------------------------------
  // One-time init.
  //-------------------------------------------------------------------------------
  useEffect(() => 
  {
    // Initializes the local data
    if(props.expression === undefined)
    {
      // If the color scheme passed in has no filter, set up a default one
      const newFilter: ILayerAttribExpression =
      {
        clauses: [],
        operator: "and"
      }
      setLocalExpression(newFilter);
    }
    else // There is a filter sent it through props - use it to initialize the local copy
      setLocalExpression(props.expression);

  }, [props.showExpressionEditor]);

  //-------------------------------------------------------------------------------
  // Cancel without saving changes.
  //-------------------------------------------------------------------------------
  const OnClose = () => 
  {
    props.setShowExpressionEditor(false);
    setLocalExpression(undefined);
    setLocalChangesWereMade(false);
  }

  //-------------------------------------------------------------------------------
  // Apply changes and close.
  //-------------------------------------------------------------------------------
  const OnAcceptChanges = async () => 
  {
    if(!localExpression)
       return;

    // Validate the search expression

    for(let i=0; i < localExpression.clauses.length; i++)
    {
      const clause: ILayerAttribExpressionClause = localExpression.clauses[i];

      if(!clause.attribute_id)
      {
        ToastNotification('error','One of the search clauses does not have an attribute selected');
        return false;
      }

      const attribute: IVectorLayerAttribute  | undefined = GetVectorLayerAttributeByID(props.layer,clause.attribute_id);
      if(!attribute) return;

      if((!clause.value || clause.value.toString().trim().length === 0) && (clause.operator !== 'is empty' && clause.operator !== 'is not empty'))
      {
        ToastNotification('error','One of the search clauses does not have a value');
        return false;
      }

      if(clause.type && clause.type === 'number' && IsNumber(clause.value) === false)
      {
        ToastNotification('error',`Attribute "${attribute?.attribute_name}" must have a numerical value`);
        return false;
      }

      if(clause.type && clause.type === 'number' && (clause.operator === 'is in range' || clause.operator === 'is not in range')  )
      {
        if(!clause.value_max)
        {
          ToastNotification('error','One of the search clauses does not have a MAX value');
          return false;
        }
    
        if(IsNumber(clause.value_max) === false)
        {
          ToastNotification('error',`Attribute "${attribute?.attribute_name}" must have a numerical MAX value`);
          return false;
        }

        if(clause.value && Number.parseFloat(clause.value_max) < Number.parseFloat(clause.value as string))
        {
          ToastNotification('error',`The MIN value cannot exceed the MAX value for attribute "${attribute?.attribute_name}"`);
          return false;
        }
      }

      // Trim whitespace for text values
      if(clause.type === 'string' && clause.value)
        clause.value = (clause.value as string).trim();
    }

    if(localChangesWereMade === true)
    {
      // Notify caller of changes
      props.OnExpressionChanged(localExpression);
    }

    // Close the window
    OnClose();
  }

  //-------------------------------------------------------------------------------
  // Renders the search clause operator for the combo box (based on the attrib type).
  //-------------------------------------------------------------------------------
  function RenderSearchClauseOperatorComboBox(searchClause: ILayerAttribExpressionClause)
  {
    if(!searchClause) return null;

    if(searchClause.type === 'string')
    return (
      LayerAttribExpressionStringOperators.map(operatorStr => 
        <MenuItem key={operatorStr} value={operatorStr}>{operatorStr}</MenuItem>
      )
    )

    if(searchClause.type === 'number')
    return (
      LayerAttribExpressionNumberOperators.map(operatorStr => 
        <MenuItem key={operatorStr} value={operatorStr}>{operatorStr}</MenuItem>
      )
    )

    return (
      <MenuItem value='is equal to'>is equal to</MenuItem>
    )
  }

  //-------------------------------------------------------------------------------
  // The user has selected a different attribute for one of the search clauses.
  //-------------------------------------------------------------------------------
  function OnAttributeChanged(event: SelectChangeEvent<any>, searchClause: ILayerAttribExpressionClause)
  {
    if(!localExpression) return;

    const clickedAttributeID: number | undefined = event.target.value;
    if(!clickedAttributeID) return;

    const attribute: IVectorLayerAttribute  | undefined = GetVectorLayerAttributeByID(props.layer,clickedAttributeID);
    if(!attribute) return;

    const newSearchClause: ILayerAttribExpressionClause = 
    {
      ...searchClause,
      attribute_id: attribute.attribute_id,
      type: attribute.data_type,
      operator: 'is equal to',
      value: '',
      value_max: ''
    }

    const updatedExpression: ILayerAttribExpression =
    {
      ...localExpression,
      clauses: localExpression.clauses.map(oldClause => oldClause.id === searchClause.id ? newSearchClause: oldClause)
    }

    // Update the local expression
    setLocalExpression(updatedExpression);

    // Keep track of changes made (so we know we need to save when the user hits "Accept Changes")
    setLocalChangesWereMade(true);
  }

  //-------------------------------------------------------------------------------
  // The user has selected a different operator for one of the search clauses.
  //-------------------------------------------------------------------------------
  function OnClauseOperatorChanged(event: SelectChangeEvent<any>, searchClause: ILayerAttribExpressionClause)
  {
    if(!localExpression) return;

    const clickedOperator: TLayerAttribExpressionStringOperator = event.target.value as TLayerAttribExpressionStringOperator;
    if(!clickedOperator) return;

    if(!searchClause.attribute_id) return;

    const attribute: IVectorLayerAttribute  | undefined = GetVectorLayerAttributeByID(props.layer,searchClause.attribute_id);
    if(!attribute) return;    

    const updatedSearchClause: ILayerAttribExpressionClause = 
    {
      ...searchClause,
      operator: clickedOperator,
    }

    // If the selected operator is 'is empty' or 'is not empty', clear out any current value
    if(clickedOperator === 'is empty' || clickedOperator === 'is not empty')
    {
      updatedSearchClause.value = '';
      updatedSearchClause.value_max = '';
    }

    const updatedExpression: ILayerAttribExpression =
    {
      ...localExpression,
      clauses: localExpression.clauses.map(oldClause => oldClause.id === searchClause.id ? updatedSearchClause: oldClause)
    }

    // Update the local filter
    setLocalExpression(updatedExpression);

    // Keep track of changes made (so we know we need to save when the user hits "Accept Changes")
    setLocalChangesWereMade(true);
  }

  //-------------------------------------------------------------------------------
  // The user is adding a new search clause.
  //-------------------------------------------------------------------------------
  function OnAddSearchClause()
  {
    if(!localExpression) return;

    if(localExpression.clauses.length >= LAYER_ATTRIBUTE_EXPRESSION_MAX_CLAUSES)
    {
      ToastNotification('error', `You cannot have more than ${LAYER_ATTRIBUTE_EXPRESSION_MAX_CLAUSES} clauses`);
      return;
    }

    // Find the current max id
    let maxID: number = 0;
    for(let i=0; i < localExpression.clauses.length; i++)
      if(localExpression.clauses[i].id > maxID)
        maxID = localExpression.clauses[i].id;

    const newClause: ILayerAttribExpressionClause =
    {
      id: maxID+1,
      attribute_id: undefined,
      type: undefined,
      operator: "is equal to",
      value: undefined
    }

    const updatedExpression: ILayerAttribExpression =
    {
      ...localExpression,
      clauses: [...localExpression.clauses, newClause]
    }

    // Update the local filter
    setLocalExpression(updatedExpression);

    // Keep track of changes made (so we know we need to save when the user hits "Accept Changes")
    setLocalChangesWereMade(true);
  }

  //-------------------------------------------------------------------------------
  // The user deleted a search clause.
  //-------------------------------------------------------------------------------
  function OnRemoveSearchClause(id: number)
  {
    if(!localExpression) return;

    const updatedExpression: ILayerAttribExpression =
    {
      ...localExpression,
      clauses: localExpression.clauses.filter(clause => clause.id !== id)
    }

    // Update the local expression
    setLocalExpression(updatedExpression);

    // Keep track of changes made (so we know we need to save when the user hits "Accept Changes")
    setLocalChangesWereMade(true);
  }

  //-------------------------------------------------------------------------------
  // The user has toggle the main search expression operator (AND vs OR).
  //-------------------------------------------------------------------------------
  function OnToggleMainOperator()
  {
    if(!localExpression) return;

    const updatedExpression: ILayerAttribExpression =
    {
      ...localExpression,
      operator: localExpression.operator === 'and' ? 'or' : 'and'
    }

    // Update the local filter
    setLocalExpression(updatedExpression);

    // Keep track of changes made (so we know we need to save when the user hits "Accept Changes")
    setLocalChangesWereMade(true);
  }

  //-------------------------------------------------------------------------------
  // Generate the label for the attribute "Value" UI element (min).
  //-------------------------------------------------------------------------------
  function GetValueLabelMin(searchClause: ILayerAttribExpressionClause): string
  {
    let label: string = searchClause.operator==='is in range' || searchClause.operator==='is not in range' ? 'Min Value' : 'Value';

    // If the attribute has "units" specified, include that in the label

    const attribute: IVectorLayerAttribute  | undefined = GetVectorLayerAttributeByID(props.layer,searchClause.attribute_id);
    if(!attribute || !attribute.unit)
      return label;

    return label + ' (' + attribute.unit + ')';
  }

  //-------------------------------------------------------------------------------
  // Generate the label for the attribute "Value" UI element (max, for ranges only).
  //-------------------------------------------------------------------------------
  function GetValueLabelMax(searchClause: ILayerAttribExpressionClause): string
  {
    let label: string = 'Max Value';

    // If the attribute has "units" specified, include that in the label

    const attribute: IVectorLayerAttribute  | undefined = GetVectorLayerAttributeByID(props.layer,searchClause.attribute_id);
    if(!attribute || !attribute.unit)
      return label;

    return label + ' (' + attribute.unit + ')';
  }

  //-------------------------------------------------------------------------------
  // The user has made changes to an attribute value for one of the search clauses.
  //-------------------------------------------------------------------------------
  function OnAttribValueChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, searchClause: ILayerAttribExpressionClause)
  {
    if(!localExpression || !searchClause.attribute_id) return;

    const newValue: string = event.target.value;

    const updatedSearchClause: ILayerAttribExpressionClause = 
    {
      ...searchClause,
      value: newValue,
    }

    const updatedExpression: ILayerAttribExpression =
    {
      ...localExpression,
      clauses: localExpression.clauses.map(oldClause => oldClause.id === searchClause.id ? updatedSearchClause: oldClause)
    }

    setLocalExpression(updatedExpression);

    // Keep track of changes made (so we know we need to save when the user hits "Accept Changes")
    setLocalChangesWereMade(true);
  }

  //-------------------------------------------------------------------------------
  // The user has made changes to an attribute's max value.
  //-------------------------------------------------------------------------------
  function OnAttribMaxValueChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, searchClause: ILayerAttribExpressionClause)
  {
    if(!localExpression || !searchClause.attribute_id) return;

    const newValue: string = event.target.value;

    const updatedSearchClause: ILayerAttribExpressionClause = 
    {
      ...searchClause,
      value_max: newValue,
    }

    const updatedExpression: ILayerAttribExpression =
    {
      ...localExpression,
      clauses: localExpression.clauses.map(oldClause => oldClause.id === searchClause.id ? updatedSearchClause: oldClause)
    }

    setLocalExpression(updatedExpression);

    // Keep track of changes made (so we know we need to save when the user hits "Accept Changes")
    setLocalChangesWereMade(true);
  }













  if(!localExpression || !props.layer) return null;

  return (

    <Dialog disablePortal open={props.showExpressionEditor===true} onClose={OnClose} maxWidth='xl' 
            PaperProps={{ sx: { minWidth: '40%', width: '40%', maxWidth: '40%', maxHeight: '90vh' }}}>

      {/* Dialog Title */}

      <DialogTitle sx={{ bgcolor: theme_bgColorLight1, justifyContent: 'space-between', pl: 2, pr: 1 }}>

        <Stack direction='row' sx={{ justifyContent: 'space-between' }}>

          <Stack>
            <Typography sx={{ fontSize: '1.3rem', fontWeight:' bold', color: theme_textColorMain }}>
              Layer Attribute Expression
            </Typography>

            {localChangesWereMade === true
              ?
                <Typography sx={{ mt: '5px', width:'90px', textAlign: 'center', fontSize: '0.6rem', color: theme_errorRed, fontWeight: 'bold', bgcolor: theme_bgColorMain, px: 0.4, borderRadius: 1, textTransform: 'none', boxShadow: 1 }}>
                  unsaved changes
                </Typography>
              :
                <Typography sx={{ mt: '5px', width:'60px', textAlign: 'center', fontSize: '0.6rem', color: theme_limeGreen, bgcolor: theme_bgColorMain, opacity: 0.7, px: 0.4, borderRadius: 1, textTransform: 'none', boxShadow: 1 }}>
                  no changes
                </Typography>
            }

          </Stack>

          <IconButton size="small" onClick={OnClose}
                      sx={{ ml: 12, padding: 0, width: '35px', height: '35px' }}>
            <CloseIcon sx={{ opacity: 0.9, width: '35px', height: '35px', color: theme_textColorBlended }} />
          </IconButton>

        </Stack>

      </DialogTitle>

      {/* Dialog Content */}

      <DialogContent sx={{ background: theme_bgColorGradient2 }}>
        <Stack sx={{ mt: 2, width: '100%' }}>

          {localExpression.clauses.length === 0
            ?
              <Typography sx={{ color: theme_textColorMain, opacity: 0.35 }}>
                There are no clauses defined.
              </Typography>
            :null
          }

          <Stack sx={{ overflow: 'auto', maxHeight: '250px', bgcolor: theme_bgColorLight1+'AA', 
                       borderRadius: 1, boxShadow: 1, px: 0.7 }}>

            {localExpression.clauses.map(searchClause =>

              <Stack key={searchClause.id} direction='column' sx={{ mt: 0.7, mb: 0.7 }}>

                <Stack direction='row' sx={{ alignItems: searchClause.operator==='is in range' || searchClause.operator==='is not in range' ? 'center' : 'flex-end' }}>

                  <Stack direction='row' sx={{ alignItems: 'flex-end' }}>

                    {/* Attribute selector */}

                    <FormControl variant="standard" sx={{ mr: 1, minWidth: 120 }}>
                      <InputLabel id="attrib-name-label" sx={{ fontSize: '0.8rem', color: theme_textColorBlended }}>Attribute</InputLabel>
                      <ParcelSearchSelect variant='standard' size='small' labelId='attrib-name-label' 
                                          value={searchClause.attribute_id} onChange={(e)=>OnAttributeChanged(e,searchClause)}>
                        {props.layer?.attributes?.map(function(attribute)
                        {
                          // Skip admin attributes
                          //if(attribute.is_admin === true) return null;

                          return (
                            <MenuItem key={attribute.attribute_id} value={attribute.attribute_id} sx={{ px: '5px' }}>
                              <Stack direction='row' sx={{ alignItems: 'center' }}>
                                <AttributeHelp iconSize='16px'
                                               name={attribute.attribute_name} 
                                               description={attribute.description}
                                               units={attribute.unit}
                                               url={attribute.info_url}/>
                                <Typography sx={{ ml: '6px' }}>
                                  {attribute.display_name}
                                </Typography>
                              </Stack>
                            </MenuItem>
                          )
                        }
                        )}
                      </ParcelSearchSelect>
                    </FormControl>

                    {/* Operator selector */}

                    <ParcelSearchSelect variant='standard' size='small' labelId='attrib-operator-label' 
                                        value={searchClause.operator} onChange={(e)=>OnClauseOperatorChanged(e,searchClause)}
                                        sx={{ fontSize: '0.8rem', mr: 1, color: theme_limeGreen }}>
                        {RenderSearchClauseOperatorComboBox(searchClause)}
                    </ParcelSearchSelect>

                  </Stack>

                  {/* Attribute Value: number, string */}

                  {searchClause.type === 'number' || searchClause.type === 'string'
                    ?
                      <Stack sx={{ width: '100%' }}>

                        <ParcelSearchTextField size='small' variant='standard' 
                            label={GetValueLabelMin(searchClause)}
                            disabled={(searchClause.operator === 'is empty' || searchClause.operator === 'is not empty')}
                            value={searchClause.value} onChange={(e)=>OnAttribValueChanged(e,searchClause)}
                            inputProps={{style: { fontSize: '1rem', color: theme_textColorMain, opacity: 0.8 }}} // font size of input text
                            InputLabelProps={{style: { fontSize: '0.8rem', color: theme_textColorBlended }}} // font size of input label
                            sx={{ minWidth: '120px', width: '100%' }}/>

                        {/* Max value (only for number ranges) */}
                        {searchClause.operator==='is in range' || searchClause.operator==='is not in range'
                          ?
                            <ParcelSearchTextField size='small' variant='standard' label={GetValueLabelMax(searchClause)}
                                value={searchClause.value_max} onChange={(e)=>OnAttribMaxValueChanged(e,searchClause)}
                                inputProps={{style: { fontSize: '1rem', color: theme_textColorMain, opacity: 0.8 }}} // font size of input text
                                InputLabelProps={{style: { fontSize: '0.8rem', color: theme_textColorBlended }}} // font size of input label
                                sx={{ minWidth: '120px', width: '100%' }}/>
                          :null
                        }
                      </Stack>
                    :null
                  }

                  {/* Attribute value: NO ATTRIBUTE SELECTED */}

                  {searchClause.type === undefined
                    ?
                      // Just a space placeholder so everything lines up in the UI
                      <Stack sx={{ width: '100%' }}>
                      </Stack>
                    :null
                  }

                  {/* Delete Clause button */}

                    <IconButton sx={{p: 0.1, ml: 1}} onClick={(_)=>OnRemoveSearchClause(searchClause.id)}>
                      <DeleteIcon sx={{ color: theme_textColorBlended, opacity: 0.7, p: 0.4 }}/>
                    </IconButton>

                </Stack>

              </Stack>
            )}

          </Stack>

          {/* Search expression botton bar: Match AND/OR, Add Clause */}

          <Stack direction='row' sx={{ mt: 3, justifyContent: 'space-between'}}>

            <Button size='small' onClick={(_)=>OnAddSearchClause()} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
              <PlaylistAddIcon sx={{ mr: 0.7, color: theme_textColorBlended, width: '20px', height: '20px' }}/>
              Add Clause
            </Button>

            <Button size='small' onClick={(_)=>OnToggleMainOperator()} sx={{ textTransform: 'none', mx: 2 }}>
              <Typography sx={{ color: theme_textColorBlended, fontSize: '0.6rem' }}>
                Match:
              </Typography>
              <Typography sx={{ color: theme_limeGreen, ml: 0.6, fontSize: '0.7rem', opacity: 0.8 }}>
                {localExpression.operator==='or'?'Any clause (OR)':'All clauses (AND)'}
              </Typography>
            </Button>

          </Stack>          

        </Stack>
      </DialogContent>

      {/* Dialog bottom bar */}

      <DialogActions sx={{ bgcolor: theme_bgColorLight1 }}>

        <Stack direction='column' sx={{ width: '100%', justifyContent: 'center', alignItems: 'center' }}>
          
          {/* CANCEL and ACCEPT CHANGES buttons */}

          <Stack direction='row'>
            <Stack sx={{ alignItems: 'center' }}>
              <Button variant='outlined' onClick={OnClose} sx={{ mr: 3, width: '100px' }}>
                Cancel
              </Button>
            </Stack>

            <Stack sx={{ alignItems: 'center' }}>
              <Button type="submit" variant='contained' sx={{ width: '200px', fontWeight: 'bold' }}
                      onClick={OnAcceptChanges}>
                Accept Changes
              </Button>
            </Stack>
          </Stack>

        </Stack>

      </DialogActions>
            
    </Dialog>
  )
}

